import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'components/ui/dialog';
import { type ComponentProps, type ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';

/*
 * The parts of a dialog that shares something with people: a widget with the firm, a draft with colleagues. It is
 * the new design's dialog in both designs. Classic opens it over its own pages: the dialog is portalled into a root
 * of its own that carries `data-design='new'`, on which `index.css` declares the new design's tokens and under which
 * the `new:` variant matches. The menus and pickers it opens portal into the dialog's portal, so they follow.
 */

type ShareDialogProps = {
    open: boolean;
    onClose: () => void;
    title: string;
    /** What is shared, under the title. */
    description: ReactNode;
    /** The dialog's width from `sm` up, which the shell sets to 720px, and its place, centred unless given. */
    className?: string;
    /** Classes on the backdrop. */
    overlayClassName?: string;
    /** Rules the header off the body. Without it the body's first section sits close under the title. */
    ruled?: boolean;
    /** The body and the footer, mounted only while the dialog is open. */
    children: ReactNode;
};

export function ShareDialog({
    open,
    onClose,
    title,
    description,
    className,
    overlayClassName,
    ruled = true,
    children,
}: ShareDialogProps) {
    // The dialog waits for its root, which is in the document from the first commit.
    const [root, setRoot] = useState<HTMLDivElement | null>(null);
    return (
        <>
            {createPortal(<div data-design="new" ref={setRoot} />, document.body)}
            <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
                <DialogContent
                    container={root}
                    overlayProps={{ className: overlayClassName }}
                    className={cn(
                        'flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden rounded-[14px] p-0 sm:max-w-[720px]',
                        className
                    )}
                >
                    {/* The right padding keeps the row clear of the dialog's own close button, which the kit pins to the
                        right edge whatever the direction: physical, as that is. */}
                    <div
                        className={cn(
                            'flex flex-col gap-0.5 pr-14 pl-6',
                            ruled ? 'border-neutral-tint border-b pt-[18px] pb-3.5' : 'pt-5 pb-3'
                        )}
                    >
                        <DialogTitle className="text-dialog-title not-new:tracking-normal font-semibold">
                            {title}
                        </DialogTitle>
                        <DialogDescription className="text-meta">{description}</DialogDescription>
                    </div>
                    {children}
                </DialogContent>
            </Dialog>
        </>
    );
}

/** The body under the header, which scrolls when the dialog runs out of height. */
export function ShareBody({ className, ...props }: ComponentProps<'div'>) {
    return <div className={cn('flex min-h-0 flex-col gap-4 overflow-y-auto px-6 py-[18px]', className)} {...props} />;
}

/** A bordered group of the body's controls. */
export function ShareSection({ className, ...props }: ComponentProps<'section'>) {
    return (
        <section
            className={cn('border-neutral-tint flex flex-col gap-3 rounded-[10px] border px-4 py-3.5', className)}
            {...props}
        />
    );
}

/** What sharing does and does not give, at the end of the body. */
export function ShareNote({ children }: { children: ReactNode }) {
    return (
        <p className="border-primary-wash-border bg-primary-wash text-primary-ink text-meta rounded-[10px] border px-3.5 py-2.5">
            {children}
        </p>
    );
}

type ShareFooterProps = {
    submitLabel: string;
    onCancel: () => void;
    onSubmit: () => void;
    disabled?: boolean;
};

/** Cancel, then the dialog's one filled action. */
export function ShareFooter({ submitLabel, onCancel, onSubmit, disabled }: ShareFooterProps) {
    return (
        <div className="border-neutral-tint flex items-center justify-end gap-2 border-t px-6 py-3.5">
            <Button variant="outline" className="border-border-control px-3.5" onClick={onCancel}>
                {t('cancel')}
            </Button>
            <Button variant="secondary" className="px-4 font-semibold" disabled={disabled} onClick={onSubmit}>
                {submitLabel}
            </Button>
        </div>
    );
}
